import { CropYear, DateValidationService, getConcurrentCrops, getFieldCroppingArea, ICrop, ICropping, ICroppingComposition, IField, isArrayEmpty, IVariety, roundArea, sum } from "../../omnia"
import { adjustDates, setCropDefaultDates } from "./dates"
import { getUpdatedPrevList } from "./history"
import { CropManagementEditContext } from "./management"

/// <reference path="dates.ts" />
/// <reference path="history.ts" />

import m = omnia.models
import { CropId, CropMaltingOption, CropPurpose, RegionId } from "@omnia/shared/generated/enums"

export interface CroppingRef {
    id: number
    descr: string
    area: number
}

export class CroppingEditContext {

    private _new: boolean;

    private _cropYear: CropYear
    public readonly cropYears: CropYear[]

    private _crop: ICrop;

    private _variety: IVariety

    private _prevlist: CroppingRef[]
    private _prev: CroppingRef

    public dateValidationService: DateValidationService;


    constructor(
        private region: RegionId,
        private target: ICropping,
        private previous: ICropping,
        private formProvider: () => ng.IFormController,
        private getCurrentArea: () => number,
        private croppingYears: number[],
        private readonly scotland = region === RegionId.Scotland) {

        this.cropYears = croppingYears.sort((a, b) => b - a).map(y => new CropYear(y))
        this._cropYear = this.cropYears.find(y => y.Year == target.YearId)

        this._new = !target.CroppingId
        this._crop = (target.Crop && target.Crop.CropId ? target.Crop : null)
        this._variety = (target.Variety && target.Variety.CropVarietyId ? target.Variety : null)

        if (this._new) {
            this.setupNewCropping(this.target.Field)
        }
        else {
            this.updateDependencies()
        }

    }

    private updateDependencies() {

        if (!this.target.Field) {
            return
        }

        this.dateValidationService = new DateValidationService(
            this.target,
            this.crop,
            this.useDestructionDate,
            this.formProvider,
            this.getCurrentArea,
            this._cropYear,
            this.croppingYears
        )

        const prev = getUpdatedPrevList(this.target, this._prev) || { prev: null, prevlist: null }

        this._prev = prev.prev
        this._prevlist = prev.prevlist

    }


    get dateErrors(): any {
        return this.dateValidationService?.dateErrors
    }

    get form() {
        return this.formProvider()
    }

    get cropYear() {
        return this._cropYear
    }

    set cropYear(value: CropYear) {
        this.changeCropYear(value)
    }

    get field(): IField {
        return this.target.Field
    }

    set field(value: IField) {

        this.target.FieldId = value ? value.FieldId : null
        this.target.Field = value

        this.updateDependencies()

        const prev = this.previous

        this.target.Area = prev
            ? prev.Area
            : this.getUsableArea()


    }

    get cropRequired() {
        return false
    }

    get previousCroppings() {
        return this._prevlist
    }

    get previousCropping() {
        return this._prev
    }

    set previousCropping(value) {
        this._prev = value
    }

    getCurrentPrevious() {

        const field = this.target.Field
        if (!field) return

        const prev = this._prev || (this._prevlist || []).sort((a, b) => b.area - a.area)[0]

        const id = prev ? prev.id : (this.previous ? this.previous.CroppingId : null)

        return id ? field.Cropping.find(c => c.CroppingId == id) : null

    }

    get crop(): ICrop {
        return this._crop;
    }

    set crop(value: ICrop) {
        this.setupCrop(value)
    }

    get originalVariety(): IVariety {
        return this._variety
    }

    get uncropped(): boolean {
        return (!this._crop) || (this._crop.CropId == 0)
    }

    get startDate(): Date {
        return this.target.SowingDate;
    }

    set startDate(value: Date) {
        this.target.SowingDate = value
        this.updateDependencies()
    }

    get endDate(): Date {
        return (this.useDestructionDate ? this.target.DestructionDate : this.target.HarvestDate)
    }

    set endDate(value: Date) {
        this.target.DestructionDate = value
        this.target.HarvestDate = value
        this.updateDependencies()
    }

    get useDestructionDate(): boolean {

        if (this.target.Destroyed) return true;

        var crop = this._crop;

        if (crop) {
            if (crop.DestructionDateReqd) return true;
        }

        return false;

    }

    useBoundaryArea(): boolean {
        return this.target && this.target.Field && (this.target.Field.AreaType == m.FieldAreaType.BoundaryArea);
    }

    canSwitchDateType(): boolean {
        return this._crop && this._crop.HarvestDateReqd;
    }

    setupCrop(crop: ICrop) {

        const target = this.target

        this.target.Crop = crop
        this._crop = crop

        if (!crop) {

            target.SowingDate = this._cropYear.StartDate
            target.HarvestDate = this._cropYear.EndDate
            target.Destroyed = false
            target.DestructionDate = undefined
        }

        else {

            if (target.Variety && (crop.CropId != target.Variety.CropId)) {
                target.Variety = null
            }

            setCropDefaultDates(target, crop, this.useDestructionDate)

            if (crop.TargetYieldReqd && crop.TargetYield) {
                target.TargetYield = crop.TargetYield
            }

            if (crop.ResidueReqd && crop.Residue) {
                target.Residue = crop.Residue
            }

            if (crop.NLeafAnalysis && (!target.NLeafAnalysis)) {
                target.NLeafAnalysis = m.NLeafAnalysis.Satisfactory
            }

        }

        adjustDates(target, this.cropYear)

        this.updateDependencies()

    }

    private setupNewCropping(field: IField) {

        this.field = field

        const prev = this.previous

        if (prev) {
            this.target.Boundary = prev.Boundary
            this.target.BoundaryArea = prev.BoundaryArea
            this.target.Area = prev.Area
        }
        else {
            this.target.Boundary = this.field.Boundary
            this.target.BoundaryArea = this.field.BoundaryArea
        }

        this.setupCrop(null)

    }

    private changeCropYear(year: CropYear) {

        if (!year) return

        this.target.YearId = year.YearId
        this._cropYear = year

        this.setupCrop(this.crop)

    }

    getUsableArea(): number {

        const field = this.field

        if (field) {

            let area = getFieldCroppingArea(field)

            const others = getConcurrentCrops(this.target)

            if (others.length) {
                const othersArea = sum(others, c => c.Area)
                area -= othersArea
            }

            area = Math.max(0, roundArea(area))

            return area

        }

    }

    getUpdatedData(single: boolean, mgmt: CropManagementEditContext): ICropping {

        const t = this.target

        const malting = this.supportsMaltingOptions()

        const data: ICropping = {

            CroppingId: 0,

            PreviousIds: undefined,
            PreviousCroppings: undefined,

            NextIds: undefined,
            NextCroppings: undefined,

            FieldId: (single && t.Field ? t.Field.FieldId : null),
            Field: undefined,

            YearId: t.YearId,

            MainPreviousId: (single && this._prev) ? this._prev.id : null,

            CropId: (t.Crop ? t.Crop.CropId : null),
            Crop: undefined,

            CropName: t.CropName,
            CropDescr: t.CropDescr,

            VarietyId: (t.Variety ? t.Variety.CropVarietyId : null),
            Variety: undefined,

            Area: (single ? t.Area : null),
            ManureSpreadArea: (single ? t.ManureSpreadArea : null),

            Boundary: undefined,
            BoundaryArea: t.BoundaryArea,

            TargetYield: t.TargetYield,

            StartDate: undefined,
            SowingDate: t.SowingDate,
            HarvestDate: ((!this.useDestructionDate) ? t.HarvestDate : null),

            EndDate: undefined,
            Destroyed: t.Destroyed,
            DestructionDate: (this.useDestructionDate ? t.DestructionDate : null),

            Residue: t.Residue,

            GrassManagement: t.GrassManagement,
            GrassCuts: t.GrassCuts,
            GrassSiteClass: t.GrassSiteClass,

            BiomassLevel: t.BiomassLevel,
            StrawMulch: t.StrawMulch,
            GrowingSeasonLength: t.GrowingSeasonLength,
            ProtectionStatus: t.ProtectionStatus,
            NLeafAnalysis: t.NLeafAnalysis,

            ActualYield: t.ActualYield,
            ActualResidue: t.ActualResidue,

            Comment: t.Comment,

            Composition: this.getUpdatedComposition(t),

            Undersown: t.Undersown,

            MaltingOption: malting ? t.MaltingOption : undefined,
            HighNDistilling: malting ? t.HighNDistilling : undefined,

            NCropGroup: t.NCropGroup,

            Market: t.Market,

            Management: isArrayEmpty(mgmt.selection)
                ? undefined
                : {
                    Options: mgmt.selection?.map(s => ({
                        ...s,
                        Area: single ? s.Area : undefined
                    }))
                },

            PlotId: t.PlotId,

            BatchId: t.BatchId,

            Grower: t.Grower,

            PlantedVolume: t.PlantedVolume,

            Description: t.Description

        }

        return data

    }

    private getUpdatedComposition(target: ICropping): ICroppingComposition[] {

        if (omnia.isArray(target.Composition)) {

            return target.Composition.map(c => {
                return {
                    CropId: (c.Crop ? c.Crop.CropId : c.CropId),
                    Percentage: c.Percentage
                }
            });

        }

    }

    public supportsMaltingOptions() {
        const { scotland, target } = this
        return scotland && (target.Variety?.Market == CropPurpose.Malting)
    }

    public supportsHighNDistilling(): boolean {
        const { target, crop } = this
        return this.supportsMaltingOptions()
            && (target.MaltingOption == CropMaltingOption.Distilling)
            && (crop?.CropId == CropId.SpringBarley)
    }


}
